import { ControllerProtocol, Injectable } from '@visiba/instantiation';
import { signal } from '@preact/signals-react';
import { Logger, Message } from '@visiba/logger';
import { FeatureFlagService } from '@application/feature_flags/feature_flag.service';
import { LicenceHolderApi } from '@api_patient/licence_holder_api.service';
import { GeneralApiService } from '@api_patient/general_api.service';
import { TokenOverrides } from '@cellula/react';
import { CriticalData, UniversalConfig } from '@application/universal_config.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { TRANSLATION_SETS } from '../../constants';
import { AuthService } from '@application/authentication/auth.service';
import { CaseService } from '@feature/cases/case.service';
import { UnitService } from '@application/unit.service';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { DateService } from '@application/time/date.service';
import { isSomeError } from '@visiba/std';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { RecentReceptionsApiService } from '@api_patient/recent_receptions_api.service';
import { ConsentService, UserConsent } from '@component/consent/consent.service';
import { NiN } from '@visiba/national-identity-number';

export enum BootState {
	Pristine = 1 << 0,
	NonFunctional = 1 << 1,
	HasCriticalInformation = 1 << 2,
	Routeable = 1 << 3,
	Completed = 1 << 4,
}

@Injectable()
export class ShellController implements ControllerProtocol {
	public bootState = signal(BootState.Pristine);

	private readonly logger = new Logger('startup');
	private readonly globalLogger = new Logger('global');

	constructor(
		private readonly featureFlagService: FeatureFlagService,
		private readonly licenceHolderApi: LicenceHolderApi,
		private readonly generalApiService: GeneralApiService,
		private readonly universalConfig: UniversalConfig,
		private readonly authService: AuthService,
		private readonly caseService: CaseService,
		private readonly unitService: UnitService,
		private readonly userSettingsService: UserSettingsService,
		private readonly dateService: DateService,
		private readonly analyticService: AnalyticService,
		private readonly recentReceptionsApiService: RecentReceptionsApiService,
		private readonly consentService: ConsentService,
	) {
		// Empty
	}

	async whenMount() {
		await this.boot();
	}

	/**
	 * Starts the application
	 * ---
	 * Initializing and distributing necessary data through globally dependant
	 * services. While not all data is required for the application to render, a
	 * portion is and that data is under a "Critical Data" section. The way the
	 * application will be able to determine when it may render is through a
	 * "bootState". This boot state is mutated through the flow of this method,
	 * and everything under "Routeable" should not invoke any React tree that's
	 * sensitive to the critical data existing.
	 *
	 * To make the experience better for the end user, keeping the amount of
	 * Critical Data down is important for us. Especially when the application
	 * is a SPA (Single Page Application) for the time being and the user needs to
	 * fetch both a main.js bundle and the booting data. We'll need to do some
	 * classic kluringar eventually to keep the load time for mobile devices as
	 * fast as possible - either smarter SPA handling or going SSR hybrid.
	 */
	public async boot() {
		this.useGlobalCatch();
		this.useAnalyticErrors();

		this.logger.logInformation('start booting process');

		// We need to boot the application with some customer.
		const urlAlias = this.getUrlAlias();

		this.logger.logInformation('loads public base');
		const patientBaseResult = await this.generalApiService.getPatientBase(urlAlias);
		if (patientBaseResult.err) return this.handleFailureOfCriticalData(patientBaseResult.err);
		const {
			Currency: _Currency,
			GlobalCustomerId,
			HideProvidedByVisibaCare,
			LanguageCode: _LanguageCode,
			LicenceHolderId,
			RootUnitId,
			DaysToDepersonalizeClientAfterRequest,
			PrivacyPolicyUrl,
			RequireClientEmail,
			RequireClientPhone,
			TermsOfServiceUrl,
			AppConfig,
		} = patientBaseResult.ok.data;

		const localeResult = await this.licenceHolderApi.getLocale(LicenceHolderId);
		if (localeResult.err) return this.handleFailureOfCriticalData(localeResult.err);
		const {
			AvailableLanguages,
			DefaultCountry,
			DefaultLanguage,
			SupportedCountries: _SupportedCountries,
			TimeZoneIana: _TimeZoneIana,
		} = localeResult.ok.data;

		this.logger.logInformation('loads theme');
		const themeResult = await this.licenceHolderApi.getTheme(patientBaseResult.ok.data.LicenceHolderId);
		if (themeResult.err) return this.handleFailureOfCriticalData(themeResult.err);

		this.logger.logInformation('check auth status');
		const identityResult = await this.authService.loadIdentity();
		if (identityResult.err) return this.handleFailureOfCriticalData(identityResult.err);

		this.universalConfig.insertCriticalData(
			new CriticalData({
				availableLanguages: AvailableLanguages,
				globalCustomerId: GlobalCustomerId,
				urlAlias: urlAlias,
				rootUnitId: RootUnitId,
				licenceHolderId: LicenceHolderId,
				initialCountryCode: DefaultCountry as keyof NiN.Implementations,
				languageCode: DefaultLanguage,
				theme: themeResult.ok.data as TokenOverrides,
				hideProvidedByVisibaCare: HideProvidedByVisibaCare,
				requireClientEmail: RequireClientEmail,
				requireClientPhone: RequireClientPhone,
				daysToDepersonalizeClientAfterRequest: DaysToDepersonalizeClientAfterRequest,
				legal: {
					tos: TermsOfServiceUrl,
					pp: PrivacyPolicyUrl,
				},
				appConfig: AppConfig
					? {
							androidPackageId: AppConfig.AndroidPackageId,
							appStoreId: AppConfig.AppStoreId,
						}
					: null,
			}),
		);

		this.bootState.value = BootState.HasCriticalInformation;

		this.userSettingsService.loadSettings();
		this.userSettingsService.setLanguage();

		// Figure out how to remove this...
		this.logger.logInformation('Unnecessarily loads unit tree');
		const unitServiceResult = await this.unitService.load(RootUnitId);
		if (unitServiceResult.err) return this.handleFailureOfCriticalData(unitServiceResult.err);

		if (this.authService.isSignedIn()) {
			const result = await this.recentReceptionsApiService.getRecentlyVisited(1);
			if (result.ok) {
				const latestVisitedReceptionId = result.ok.data[0]?.ReceptionId ?? null;

				this.universalConfig.recentlyVisitedReceptionId.value = latestVisitedReceptionId;
			}
		}

		this.logger.logInformation('loads translation set');

		const getTranslationSetResults = await this.generalApiService.getTranslationSets(
			LicenceHolderId,
			[...TRANSLATION_SETS],
			this.userSettingsService.getSettings().selectedLanguage,
		);

		if (getTranslationSetResults.err) {
			return this.handleFailureOfCriticalData(getTranslationSetResults.err);
		}

		I18nAndL10n.insertTranslations(getTranslationSetResults.ok.data);

		// Set locale for date service
		await this.dateService.loadLocale(this.userSettingsService.getSettings().selectedLanguage);

		this.logger.logInformation('loads feature flags');
		const featureFlagResult = await this.featureFlagService.load(urlAlias);
		if (featureFlagResult.err) {
			this.logger.logError('failed to load feature flags');
		}

		// Keep current behaviour of consent if the toggle is not activated.
		if (!this.featureFlagService.checkFlag('PatientWeb.UseNewConsent')) {
			this.consentService.setDefault(UserConsent.Essential, UserConsent.Analytic, UserConsent.Functional);
		} else {
			this.consentService.setDefault(UserConsent.Essential);
		}

		this.consentService.on('onConsent', () => {
			this.analyticService.createTracker();
		});

		this.analyticService.setup(LicenceHolderId, GlobalCustomerId);

		// This case polling is a side effect of that we don't have a proper api call to
		// fetch notification count for patient cases.
		// TODO: Fix a better solution!
		if (identityResult.ok) {
			const d = this.caseService.pollData(() => {
				d();
			});
		}

		this.bootState.value = BootState.Routeable;
		this.logger.logInformation('finished booting process');

		return {
			unitServiceResult: unitServiceResult.ok,
			featureToggles: featureFlagResult.ok,
			localeResult: localeResult.ok.data,
			patientBaseResult: patientBaseResult.ok.data,
			themeResult: themeResult.ok.data,
			identityResult: identityResult.ok,
			getTranslationSetResult: getTranslationSetResults.ok.data,
		};
	}

	private useGlobalCatch() {
		this.globalLogger.logInformation('set up global event emitters');
		globalThis.addEventListener('error', (err) => {
			this.globalLogger.logFatal('error: {error}', err);
		});
		globalThis.addEventListener('unhandledrejection', (err) => {
			this.globalLogger.logFatal('unhandledrejection {error}', err);
		});
	}

	private useAnalyticErrors() {
		function serializeMessage(message: Message): string {
			try {
				const sendData: Message = {
					ctx: message.ctx,
					data: message.data.map((someValue: unknown) => {
						if (someValue instanceof ErrorEvent) {
							const err = someValue.error;

							const serializedError = JSON.stringify(err, Object.getOwnPropertyNames(err));

							return JSON.parse(serializedError);
						}

						if (someValue instanceof PromiseRejectionEvent) {
							const someReason = someValue.reason;

							if (isSomeError(someValue)) {
								const serializedError = JSON.stringify(someReason, Object.getOwnPropertyNames(someReason));

								return JSON.parse(serializedError);
							}

							return someReason;
						}

						if (isSomeError(someValue)) {
							const serializedError = JSON.stringify(someValue, Object.getOwnPropertyNames(someValue));

							return JSON.parse(serializedError);
						}

						return someValue;
					}),
					level: message.level,
					template: message.template,
					timestamp: message.timestamp,
				};

				return JSON.stringify(sendData);
			} catch {
				return '<could not parse>';
			}
		}

		Logger.on('fatal', (data) => {
			this.analyticService.trackEvent(AnalyticCategories.UnhandledError, data[1].template, serializeMessage(data[1]));
		});
		Logger.on('error', (data) => {
			this.analyticService.trackEvent(AnalyticCategories.Error, data[1].template, serializeMessage(data[1]));
		});
	}

	private handleFailureOfCriticalData(err: Error) {
		I18nAndL10n.derived({
			'View.NonRecoverableError.Text': 'Unexpected error',
			'View.NonRecoverableError.Heading': 'error',
		});

		this.bootState.value = BootState.NonFunctional;

		this.logger.logFatal('failed to boot application, reason: {reason}', err.message);

		return null;
	}

	private getUrlAlias(): string {
		let urlAlias = location.pathname.replace(`/v/`, '');
		if (urlAlias.charAt(0) == '/') {
			urlAlias = urlAlias.substring(1);
		}
		urlAlias = urlAlias.split('/')[0];

		return urlAlias;
	}
}
